/**
 * Texture loading for mannequin models.
 *
 * Loads texture URLs declared in {@link MannequinModel.textures} and returns
 * resolved {@link TextureSlots} suitable for the shared material factory.
 *
 * Textures are loaded once per model via Three.js TextureLoader, assigned the
 * correct color space per role, and disposed on unmount.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import * as THREE from "three/webgpu";
import type { MannequinModel } from "../mannequin/types.ts";
import type { TextureSlots } from "./createViewportMaterial.ts";
import { getMaxAnisotropy } from "./materialCache.ts";

/** Maximum texture size for mannequin textures. WebGPU guarantees ≥8192. */
const MAX_TEXTURE_SIZE = 4096;

// ---------------------------------------------------------------------------
// Texture loading
// ---------------------------------------------------------------------------

type ColorSpace = "srgb" | "linear";

const ROLE_COLOR_SPACE: Record<
  keyof NonNullable<MannequinModel["textures"]>,
  ColorSpace
> = {
  baseColor: "srgb",
  normal: "linear",
  roughness: "linear",
  metallic: "linear",
  emission: "srgb",
};

function configureTexture(tex: THREE.Texture, role: ColorSpace): void {
  tex.colorSpace =
    role === "srgb" ? THREE.SRGBColorSpace : THREE.LinearSRGBColorSpace;
  tex.flipY = false;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.generateMipmaps = true;
  tex.anisotropy = getMaxAnisotropy();
}

/**
 * Load a single texture from a URL, applying correct color space and filtering.
 * Returns a promise that resolves to the configured texture.
 */
function loadTexture(url: string, role: ColorSpace): Promise<THREE.Texture> {
  return new Promise((resolve, reject) => {
    new THREE.TextureLoader().load(
      url,
      (tex) => {
        configureTexture(tex, role);

        // Down-scale if oversized (matches SceneRenderer behavior)
        const img = tex.image as HTMLImageElement | ImageBitmap;
        const longest = Math.max(img.width ?? 0, img.height ?? 0);
        if (longest > MAX_TEXTURE_SIZE) {
          const scale = MAX_TEXTURE_SIZE / longest;
          const w = Math.round((img.width ?? longest) * scale);
          const h = Math.round((img.height ?? longest) * scale);
          createImageBitmap(
            img,
            0,
            0,
            img.width ?? longest,
            img.height ?? longest,
            {
              resizeWidth: w,
              resizeHeight: h,
              resizeQuality: "high",
            },
          )
            .then((resized) => {
              tex.image = resized as any;
              tex.needsUpdate = true;
              resolve(tex);
            })
            .catch(reject);
        } else {
          resolve(tex);
        }
      },
      undefined,
      reject,
    );
  });
}

// ---------------------------------------------------------------------------
// React hook
// ---------------------------------------------------------------------------

/**
 * Load textures declared in a mannequin model definition.
 * Returns `TextureSlots` that update reactively as textures finish loading.
 * Disposes all textures on unmount or model change.
 */
export function useMannequinTextures(
  model: MannequinModel,
): TextureSlots | undefined {
  const textureUrls = model.textures;

  // Stable key — only re-load when the actual URL values change
  const urlKey = useMemo(() => {
    if (!textureUrls) return null;
    return JSON.stringify(textureUrls);
  }, [textureUrls]);

  const [slots, setSlots] = useState<TextureSlots | undefined>(undefined);
  const loadedRef = useRef<THREE.Texture[]>([]);

  useEffect(() => {
    // Dispose previous textures
    for (const tex of loadedRef.current) tex.dispose();
    loadedRef.current = [];
    setSlots(undefined);

    if (!textureUrls || !urlKey) return;

    let cancelled = false;
    const pending: TextureSlots = {};

    const entries = Object.entries(textureUrls) as [
      keyof typeof ROLE_COLOR_SPACE,
      string,
    ][];
    for (const [role, url] of entries) {
      if (!url) continue;
      const colorSpace = ROLE_COLOR_SPACE[role];
      loadTexture(url, colorSpace).then((tex) => {
        if (cancelled) {
          tex.dispose();
          return;
        }
        loadedRef.current.push(tex);
        pending[role] = tex;
        // Spread to trigger new reference on each load completion
        setSlots({ ...pending });
      });
    }

    return () => {
      cancelled = true;
      for (const tex of loadedRef.current) tex.dispose();
      loadedRef.current = [];
    };
  }, [urlKey, textureUrls]); // eslint-disable-line react-hooks/exhaustive-deps

  return slots;
}
